import { Box } from '@mui/material';
import { getActiveMoney } from 'lib/api/express/report';
import { ActiveMoneyResponse } from 'lib/api/express/report/types';
import { Filter } from 'lib/common/GlobalSearcher';
import PageHeader from 'lib/designSystem/PageHeader';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { errorHandler } from 'lib/functions/errors';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

type SearchFilters =
  | {
      startOperationDate?: string | null;
      endOperationDate?: string | null;
      startPaidAt?: string | null;
      endPaidAt?: string | null;
      startPaymentDate?: string | null;
      endPaymentDate?: string | null;
      dataType?: 'active' | 'completed' | 'all';
      documentType?: 'all' | 'factoring' | 'credit' | 'confirming';
      stakeholderName?: string | null;
      stakeholderIdentifier?: string | null;
      requestId?: string | null;
      folio?: string | null;
    }
  | undefined;

const Report = () => {
  const dispatch = useDispatch();
  const domain = window.location.hostname;
  const secretId = domain.split('.')[0];
  const [isPending, setIsPending] = useState(false);
  const [data, setData] = useState<ActiveMoneyResponse['data']>([]);
  const [pagination, setPagination] = useState<
    ActiveMoneyResponse['pagination']
  >({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });
  const [searchFilters, setSearchFilters] = useState<SearchFilters>();

  const generateQuery = () => {
    const query = new URLSearchParams();
    if (searchFilters?.startOperationDate)
      query.set('startOperationDate', searchFilters.startOperationDate);
    if (searchFilters?.endOperationDate)
      query.set('endOperationDate', searchFilters.endOperationDate);
    if (searchFilters?.startPaidAt)
      query.set('startPaidAt', searchFilters.startPaidAt);
    if (searchFilters?.endPaidAt)
      query.set('endPaidAt', searchFilters.endPaidAt);
    if (searchFilters?.startPaymentDate)
      query.set('startPaymentDate', searchFilters.startPaymentDate);
    if (searchFilters?.endPaymentDate)
      query.set('endPaymentDate', searchFilters.endPaymentDate);
    if (searchFilters?.dataType) query.set('dataType', searchFilters.dataType);
    if (searchFilters?.documentType)
      query.set('documentType', searchFilters.documentType);
    if (searchFilters?.stakeholderName)
      query.set('stakeholderName', searchFilters.stakeholderName);
    if (searchFilters?.stakeholderIdentifier)
      query.set('stakeholderIdentifier', searchFilters.stakeholderIdentifier);
    if (searchFilters?.requestId)
      query.set('requestId', searchFilters.requestId);
    if (searchFilters?.folio) query.set('folio', searchFilters.folio);
    if (pagination.page) query.set('page', pagination.page.toString());
    if (pagination.pageSize)
      query.set('pageSize', pagination.pageSize.toString());
    return query.toString();
  };

  const filters: Filter[] = [
    {
      label: 'Fecha de solicitud',
      type: 'date',
      value: searchFilters?.startOperationDate,
      setter: (v: string | null) =>
        setSearchFilters((prev) => ({
          ...prev,
          startOperationDate: v,
        })),
      dateKey: 'startDate',
      dateType: 'request',
    },
    {
      label: 'Fecha de solicitud',
      type: 'date',
      value: searchFilters?.endOperationDate,
      setter: (v: string | null) =>
        setSearchFilters((prev) => ({
          ...prev,
          endOperationDate: v,
        })),
      dateKey: 'endDate',
      dateType: 'request',
    },
    {
      label: 'Fecha de pago',
      type: 'date',
      value: searchFilters?.startPaidAt,
      setter: (v: string | null) =>
        setSearchFilters((prev) => ({
          ...prev,
          startPaidAt: v,
        })),
      dateKey: 'startPaidDate',
      dateType: 'payment',
    },
    {
      label: 'Fecha de pago',
      type: 'date',
      value: searchFilters?.endPaidAt,
      setter: (v: string | null) =>
        setSearchFilters((prev) => ({
          ...prev,
          endPaidAt: v,
        })),
      dateKey: 'endPaidDate',
      dateType: 'payment',
    },
    {
      label: 'Fecha de vencimiento',
      type: 'date',
      value: searchFilters?.startPaymentDate,
      setter: (v: string | null) =>
        setSearchFilters((prev) => ({
          ...prev,
          startPaymentDate: v,
        })),
      dateKey: 'startPaymentDate',
      dateType: 'payment',
    },
    {
      label: 'Fecha de vencimiento',
      type: 'date',
      value: searchFilters?.endPaymentDate,
      setter: (v: string | null) =>
        setSearchFilters((prev) => ({
          ...prev,
          endPaymentDate: v,
        })),
      dateKey: 'endPaymentDate',
      dateType: 'payment',
    },
    {
      label: 'Tipo de cartera',
      type: 'select',
      value: searchFilters?.dataType,
      setter: (v: 'active' | 'completed' | 'all') =>
        setSearchFilters((prev) => ({
          ...prev,
          dataType: v,
        })),
      options: [
        { label: 'Activa', value: 'active' },
        { label: 'Completada', value: 'completed' },
        { label: 'Todas', value: 'all' },
      ],
    },
    {
      label: 'Tipo de documento',
      type: 'select',
      value: searchFilters?.documentType,
      setter: (v: 'all' | 'factoring' | 'credit' | 'confirming') =>
        setSearchFilters((prev) => ({
          ...prev,
          documentType: v,
        })),
      options: [
        { label: 'Todos', value: 'all' },
        { label: 'Factoring', value: 'factoring' },
        { label: 'Crédito', value: 'credit' },
        { label: 'Confirming', value: 'confirming' },
      ],
    },
    {
      label: 'Nombre deudor',
      type: 'autocomplete',
      value: searchFilters?.stakeholderName,
      key: 'stakeholderName',
      setter: (v: string) =>
        setSearchFilters((prev) => ({
          ...prev,
          stakeholderName: v,
        })),
    },
    {
      label: 'Rut contraparte',
      type: 'text',
      value: searchFilters?.stakeholderIdentifier,
      setter: (v: string) =>
        setSearchFilters((prev) => ({
          ...prev,
          stakeholderIdentifier: v,
        })),
    },
    {
      label: 'Id de solicitud',
      type: 'text',
      value: searchFilters?.requestId,
      setter: (v: string) =>
        setSearchFilters((prev) => ({
          ...prev,
          requestId: v,
        })),
    },
    {
      label: 'Folio',
      type: 'text',
      value: searchFilters?.folio,
      setter: (v: string) =>
        setSearchFilters((prev) => ({
          ...prev,
          folio: v,
        })),
    },
  ];

  const fetchData = async () => {
    try {
      setIsPending(true);
      const query = generateQuery();
      const response = await getActiveMoney(dispatch, secretId, query);
      if (response) {
        setData(response.data);
        setPagination(response.pagination);
      }
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setIsPending(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, []);

  return (
    <Box>
      <PageHeader.WithTitleAndActions
        title="Cartera"
        iconType="dashboard"
        subtitle="Aca podras revisar tu cartera actual para todos los productos"
        globalSearcherProps={{
          filters,
          triggerSearch: fetchData,
          dispatch,
          disableSearch: isPending,
        }}
      />
      <TableWithPagination
        dataHeaders={[
          { key: 'businessName', label: 'Nombre cliente' },
          { key: 'businessIdentifier', label: 'Rut cliente', type: 'report' },
          { key: 'stakeholderName', label: 'Nombre deudor' },
          { key: 'stakeholderIdentifier', label: 'Rut deudor' },
          { key: 'operationId', label: 'Id solicitud', type: 'numeric-id' },
          { key: 'operationType', label: 'Tipo solicitud' },
          { key: 'issuedDate', label: 'Emisión', type: 'date' },
          { key: 'documentNumber', label: 'Folio' },
          { key: 'invoiceAmount', label: 'Monto original', type: 'money' },
          { key: 'amount', label: 'Monto documento', type: 'money' },
          {
            key: 'anticipatedAmount',
            label: 'Monto anticipado',
            type: 'money',
          },
          { key: 'dueDate', label: 'Vencimiento', type: 'date' },
          { key: 'documentState', label: 'Estado documento' },
          {
            key: 'lastConciliationDate',
            label: 'Fecha último pago',
            type: 'date',
          },
          { key: 'lastConciliationPaidBy', label: 'Último pagador' },
          { key: 'conciliationAmount', label: 'Abono', type: 'money' },
          {
            key: 'documentRemainingAmount',
            label: 'Saldo documento',
            type: 'money',
          },
          {
            key: 'documentRemainingAmountNet',
            label: 'Saldo financiado',
            type: 'money',
          },
          { key: 'arrearAmount', label: 'Mora', type: 'money' },
          {
            key: 'totalRemainingNet',
            label: 'Saldo financiado + mora',
            type: 'money',
          },
          {
            key: 'creditNoteAmount',
            label: 'Monto nota crédito',
            type: 'money',
          },
          { key: 'arrearDays', label: 'Días mora' },
          { key: 'arrearAmount', label: 'Interés mora', type: 'money' },
          { key: 'cancelDate', label: 'Rechazo', type: 'date' },
        ]}
        data={data}
        rowsPerPageDefault={100}
        loading={isPending}
        searchData={fetchData}
        exportToFile="xlsx"
        setLoading={setIsPending}
        pagination={pagination}
        setPagination={setPagination}
        setData={setData}
        lastElement
      />
    </Box>
  );
};

export default Report;
